import {
  DialogDisclosure,
  DialogStateReturn,
  Popover,
  PopoverArrow,
  PopoverDisclosure,
  usePopoverState,
} from "reakit"
import cx from "classnames"

import WizardCard from "../../components/WizardCard"
import CaptionList from "../../components/CaptionList"
import NumberCard from "../../components/NumberCard"
import useMediaQuery from "../../hooks/useMediaQuery"
import IMG_STACK from "../../images/stacks.png"

export interface GovtIntroductionNavigatingYourOptionsCardProps {
  dialog: DialogStateReturn
}

const GovtIntroductionNavigatingYourOptionsCard = ({
  dialog,
}: GovtIntroductionNavigatingYourOptionsCardProps) => {
  const isMaxWidthSm = useMediaQuery("(max-width: 664px)")
  const programPopover = usePopoverState({ animated: 250, placement: "top" })
  const practicesPopover = usePopoverState({ animated: 250, placement: "top" })

  return (
    <WizardCard paddingSize={isMaxWidthSm ? "sm" : "lg-y"}>
      <div className="flex flex-col">
        <h2 className="text-2xl font-display font-medium leading-[124%] tracking-[0.48px]">
          Navigating your options
        </h2>

        <div>
          <div className="text-black/80 text-sm leading-[140%] tracking-[0.28px] space-y-3 mt-[27px]">
            Applying for{" "}
            <PopoverDisclosure
              {...programPopover}
              className="link hover:cursor-pointer mb-0"
            >
              government land programs
            </PopoverDisclosure>
            <Popover
              {...programPopover}
              tabIndex={0}
              aria-label="Locked"
              className="w-[400px] max-w-full bg-charcoal-500 text-white rounded-sm ring-0 shadow-md p-3"
            >
              <PopoverArrow
                {...programPopover}
                size={20}
                className="fill-charcoal-500"
              />

              <p className="text-white">
                A program is a set of related measures or activities with a
                particular long-term aim. Specifically here, they are
                administered by NRCS within the US government. EQIP and CSP are
                different programs tailored to different types of land and
                landowner situations.
              </p>
            </Popover>{" "}
            is cumbersome and confusing.
          </div>

          <div className="text-black/80 text-sm leading-[140%] tracking-[0.28px] space-y-3 mt-3">
            It's difficult to navigate the different government agencies,
            programs to choose from, application steps, and the sheer volume of{" "}
            <PopoverDisclosure
              {...practicesPopover}
              className="link hover:cursor-pointer mb-0"
            >
              practices
            </PopoverDisclosure>
            <Popover
              {...practicesPopover}
              tabIndex={0}
              aria-label="Locked"
              className="w-[400px] max-w-full bg-charcoal-500 text-white rounded-sm ring-0 shadow-md p-3"
            >
              <PopoverArrow
                {...practicesPopover}
                size={20}
                className="fill-charcoal-500"
              />

              <p className="text-white">
                These are specific activities you choose to implement on your
                land based on your goals and your land’s unique characteristics.
                For example, creating fire breaks, replanting native trees, and
                implementing snags for wildlife habitat are some of the unique
                practices you can add to your funding application.
              </p>
            </Popover>{" "}
            available, making it hard to get government funding to improve your
            land.
          </div>

          <p className="text-black/80 text-sm leading-[140%] tracking-[0.28px] space-y-3 mt-3">
            Our guide makes this easier by offering curated NRCS practice
            "stacks" for you to choose from, all tailored to your goals and your
            land's unique eligibility
          </p>
        </div>

        <div className="mt-14">
          <h3 className="font-body font-bold leading-[140%] tracking-[0.32px]">
            How this works
          </h3>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:pr-[6px] mt-3">
            <NumberCard
              number={1}
              heading="Your goal"
              text="We start with your land goals to focus programs that help you accomplish it."
              className={cx(
                "relative after:content-['+'] after:absolute after:w-6 after:h-6 after:flex after:items-center after:justify-center after:text-3xl after:font-display after:font-medium after:text-black",
                "after:-bottom-6 lg:after:-bottom-auto lg:after:top-1/2 after:right-1/2 lg:after:-right-6 after:translate-x-1/2 lg:after:-translate-x-0 lg:after:-translate-y-1/2"
              )}
            />

            <NumberCard
              number={2}
              heading="Your eligibility"
              text="Programs have rules, and we make sure to show you ones you fit."
              className={cx(
                "relative after:content-['+'] after:absolute after:w-6 after:h-6 after:flex after:items-center after:justify-center after:text-3xl after:font-display after:font-medium after:text-black",
                "after:-bottom-6 lg:after:-bottom-auto lg:after:top-1/2 after:right-1/2 lg:after:-right-6 after:translate-x-1/2 lg:after:-translate-x-0 lg:after:-translate-y-1/2"
              )}
            />

            <NumberCard
              number={3}
              heading="Best suited to your land"
              text="The type of land you have determines the programs you are eligible for."
            />
          </div>

          <CaptionList
            list={[
              "We will guide you through 4 eligibility questions that make sure you are offered only what you qualify for.",
              <>
                Based on the{" "}
                <DialogDisclosure
                  {...dialog}
                  className="link hover:cursor-pointer"
                >
                  goal(s)
                </DialogDisclosure>{" "}
                we have for you on file, we will prioritize the practices that
                support your goals.
              </>,
              "You will end up with a handpicked selection of practices that you can apply for.",
              "This will help save you time at the local NRCS office when you work with an agent.",
            ]}
            clasName="text-charcoal-500 mt-3"
            itemClass="text-sm !tracking-[0.28px] before:!top-0"
          />
        </div>

        <div className="mt-[27px]">
          <h3 className="font-body font-bold leading-[140%] tracking-[0.32px]">
            What is so special about our practice stacks?
          </h3>

          <div className="flex flex-col sm:flex-row gap-4 sm:gap-15 mt-[14px]">
            <div className="shrink-0">
              <img
                src={IMG_STACK}
                alt="image-stacks"
                className="block w-full h-auto"
                data-testid="image-stack"
              />
            </div>

            <div className="text-charcoal-500 font-body text-sm leading-[140%] tracking-[0.28px] space-y-3">
              <p>
                <strong>→</strong> Our stacks have been hand curated by NCX
                Forestry experts to increase your likelihood of getting funded.
              </p>

              <p>
                <strong>→</strong> Each bundle is scoped appropriately for
                successful implementation.
              </p>

              <p>
                <strong>→</strong> Stacks are built to align with your land
                goals and make them achievable.
              </p>
            </div>
          </div>
        </div>
      </div>
    </WizardCard>
  )
}

export default GovtIntroductionNavigatingYourOptionsCard
